import { useState, useRef, useCallback, useEffect } from "react";
import { FormikProps } from "formik";
import { ValidateCustomSourceApi } from "@/database";
import { useAppSelector } from "@/store/index";
import { findURLType, extractUrlFromEmbed } from "../../Create/utils";
import { FILE_INACCESSIBLE_ERROR, GDRIVE, APP_EMAIL } from "../../Create/utils";
import { CustomVideoForm } from "../Video";

interface UseUrlValidationProps {
  formik: FormikProps<CustomVideoForm>;
  uploadType: string;
  selectedFile: any;
  onValidatingUrlChange?: (isValidating: boolean) => void;
}

interface UseUrlValidationReturn {
  isValidUrl: boolean | null;
  isValidPermission: boolean;
  isValidatingUrl: boolean;
  fileName: string | null;
  youtubedescription: string | null;
  youtubetags: string[];
  youtubethumbnail: string | null;
  fileType: string | null;
  gdriveMD5Checksum: string | null;
  showRemove: boolean;
  channelWarning: string | null;
  setIsValidUrl: React.Dispatch<React.SetStateAction<boolean | null>>;
  setIsValidPermission: React.Dispatch<React.SetStateAction<boolean>>;
  setFileName: React.Dispatch<React.SetStateAction<string | null>>;
  setYoutubeDescription: React.Dispatch<React.SetStateAction<string | null>>;
  setYoutubeTags: React.Dispatch<React.SetStateAction<string[]>>;
  setYoutubeThumbnail: React.Dispatch<React.SetStateAction<string | null>>;
  setShowRemove: React.Dispatch<React.SetStateAction<boolean>>;
  setFileType: React.Dispatch<React.SetStateAction<string | null>>;
  setGdriveMD5Checksum: React.Dispatch<React.SetStateAction<string | null>>;
  handleUrlValidation: (
    urlToValidate?: string
  ) => Promise<{ success: boolean; error?: string; data?: any }>;
  resetUrlValidation: () => void;
  isValidatingUrlRef: React.MutableRefObject<boolean>;
  valuesRef: React.MutableRefObject<{
    fileName: string | null;
    youtubedescription: string | null;
    youtubetags: string[];
    youtubethumbnail: string | null;
    gdriveMD5Checksum: string | null;
  }>;
}

export const useUrlValidation = ({
  formik,
  uploadType,
  selectedFile,
  onValidatingUrlChange,
}: UseUrlValidationProps): UseUrlValidationReturn => {
  const [isValidUrl, setIsValidUrl] = useState<boolean | null>(null);
  const [isValidPermission, setIsValidPermission] = useState<boolean>(true);
  const [fileName, setFileName] = useState<string | null>("");
  const [youtubedescription, setYoutubeDescription] = useState<string | null>(
    ""
  );
  const [youtubetags, setYoutubeTags] = useState<string[]>([]);
  const [youtubethumbnail, setYoutubeThumbnail] = useState<string | null>(null);
  const [showRemove, setShowRemove] = useState(false);
  const [isValidatingUrl, setIsValidatingUrl] = useState(false);
  const [fileType, setFileType] = useState<string | null>(null);
  const [gdriveMD5Checksum, setGdriveMD5Checksum] = useState<string | null>(
    null
  );
  const [channelWarning, setChannelWarning] = useState<string | null>(null);
  const isValidatingUrlRef = useRef(false);
  const socialChannelsList = useAppSelector(
    (s) => s.app.userSocialChannels.socialChannelsList
  );

  // Refs to store latest values for synchronous access
  const valuesRef = useRef({
    fileName: null as string | null,
    youtubedescription: null as string | null,
    youtubetags: [] as string[],
    youtubethumbnail: null as string | null,
    gdriveMD5Checksum: null as string | null,
  });

  const handleUrlValidation = useCallback(
    async (
      urlToValidate?: string
    ): Promise<{ success: boolean; error?: string; data?: any }> => {
      if (isValidatingUrlRef.current) {
        return { success: false, error: "Validation already in progress" };
      }
      if (selectedFile) {
        return { success: false, error: "File already selected" };
      }

      const rawUrl = urlToValidate || formik.values.url;
      if (!rawUrl) {
        return { success: false, error: "No URL provided" };
      }
      const url = extractUrlFromEmbed(rawUrl);

      isValidatingUrlRef.current = true;
      setIsValidatingUrl(true);
      onValidatingUrlChange?.(true);
      const type = findURLType(url);

      try {
        const res = await ValidateCustomSourceApi({
          url: url,
          type: type,
          content: uploadType === "audio" ? "audio" : "video",
        });
        if (res.status === 200) {
          setIsValidUrl(true);
          setShowRemove(true);
          formik.setFieldError("url", undefined);
          setChannelWarning(null);

          let newFileName: string | null = null;
          let newYoutubeDescription: string | null = null;
          let newYoutubeTags: string[] = [];
          let newYoutubeThumbnail: string | null = null;
          let newGdriveMD5Checksum: string | null = null;

          if (type === "gdrive") {
            newFileName = res?.data?.Details?.Name;
            newGdriveMD5Checksum = res?.data?.Details?.MD5Checksum || null;
            setFileName(newFileName);
            setGdriveMD5Checksum(newGdriveMD5Checksum);
          } else if (type === "ftp") {
            newFileName = res?.result;
            newGdriveMD5Checksum = res?.result || null;
            setFileName(newFileName);
            setGdriveMD5Checksum(newGdriveMD5Checksum);
          } else if (type === "youtube_download") {
            newFileName = res?.result?.title;
            newYoutubeDescription = res?.result?.desc;
            newYoutubeTags = res?.result?.tags;
            newYoutubeThumbnail = res?.result?.thumbnail || null;
            newGdriveMD5Checksum = res?.result?.id || null;
            setFileName(newFileName);
            setYoutubeDescription(newYoutubeDescription);
            setYoutubeTags(newYoutubeTags);
            setYoutubeThumbnail(newYoutubeThumbnail);
            setGdriveMD5Checksum(newGdriveMD5Checksum);
            const videoChannelId = res?.result?.channel;
            const videoChannelName = res?.result?.channel_name;
            if (videoChannelId) {
              const isOwnChannel = (socialChannelsList || []).some(
                (c) => c.channel === videoChannelId
              );
              if (!isOwnChannel) {
                setChannelWarning(
                  videoChannelName
                    ? `This video belongs to "${videoChannelName}" and not to your channel.`
                    : "This video belongs to a channel that is not connected to your account."
                );
              }
            }
          } else if (type === "wetransfer") {
            newGdriveMD5Checksum = res?.result;
            setGdriveMD5Checksum(newGdriveMD5Checksum);
          } else if (type === "stream" || type === "httpdownload") {
            newGdriveMD5Checksum = Date.now().toString();
            setGdriveMD5Checksum(newGdriveMD5Checksum);
          }

          // Update refs immediately for synchronous access
          valuesRef.current = {
            fileName: newFileName,
            youtubedescription: newYoutubeDescription,
            youtubetags: newYoutubeTags,
            youtubethumbnail: newYoutubeThumbnail,
            gdriveMD5Checksum: newGdriveMD5Checksum,
          };

          if (newFileName) {
            formik.setFieldValue("title", newFileName);
          }
          if (newYoutubeDescription) {
            formik.setFieldValue("desc", newYoutubeDescription);
          }

          setFileType(type);
          return { success: true, data: res.data };
        } else if (res.status === 412) {
          setIsValidUrl(false);
          setIsValidPermission(false);
          formik.setFieldError(
            "url",
            "To access the above URL, please give edit access to  your-service-account@project.iam.gserviceaccount.com"
          );
          return { success: false, error: "Permission denied" };
        } else {
          if (res.error === FILE_INACCESSIBLE_ERROR && type === GDRIVE) {
            formik.setFieldError(
              "url",
              `Please allow this ${APP_EMAIL} to access your file`
            );
          }
          setIsValidUrl(false);
          formik.setFieldError("url", res.error);
          return { success: false, error: res.error };
        }
      } catch (_error) {
        setIsValidUrl(false);
        formik.setFieldError("url", "Validation failed. Please try again.");
        return { success: false, error: "Validation failed" };
      } finally {
        isValidatingUrlRef.current = false;
        setIsValidatingUrl(false);
        onValidatingUrlChange?.(false);
      }
    },
    [
      formik,
      uploadType,
      selectedFile,
      onValidatingUrlChange,
      socialChannelsList,
    ]
  );

  // Keep refs in sync with state values (backup for when setters are called directly)
  useEffect(() => {
    valuesRef.current = {
      fileName,
      youtubedescription,
      youtubetags,
      youtubethumbnail,
      gdriveMD5Checksum,
    };
  }, [
    fileName,
    youtubedescription,
    youtubetags,
    youtubethumbnail,
    gdriveMD5Checksum,
  ]);

  const resetUrlValidation = useCallback(() => {
    setIsValidUrl(null);
    setIsValidPermission(true);
    setFileName("");
    setYoutubeDescription("");
    setYoutubeTags([]);
    setYoutubeThumbnail(null);
    setShowRemove(false);
    setFileType(null);
    setGdriveMD5Checksum(null);
    setChannelWarning(null);
    // Reset refs as well
    valuesRef.current = {
      fileName: null,
      youtubedescription: null,
      youtubetags: [],
      youtubethumbnail: null,
      gdriveMD5Checksum: null,
    };
    formik.setFieldTouched("url", false);
    formik.setFieldError("url", "");
  }, [formik]);

  return {
    isValidUrl,
    isValidPermission,
    isValidatingUrl,
    fileName,
    youtubedescription,
    youtubetags,
    youtubethumbnail,
    fileType,
    gdriveMD5Checksum,
    showRemove,
    channelWarning,
    setIsValidUrl,
    setIsValidPermission,
    setFileName,
    setYoutubeDescription,
    setYoutubeTags,
    setYoutubeThumbnail,
    setShowRemove,
    setFileType,
    setGdriveMD5Checksum,
    handleUrlValidation,
    resetUrlValidation,
    isValidatingUrlRef,
    valuesRef,
  };
};
